/**
 * GuildWorldModule — GUILDS in the world (2026-09-24): each guild's CLAIM marked on the ground by banner posts along
 * its rim (your guild cyan, other player guilds violet, a versona trade guild in its trade's colour), and every
 * VERSONA trade guild's hall — the guild hall model at ×20 (VERSONA_HALL_SCALE) on its baked seat, door to the town —
 * with a collider, a vehicle solid, a terrain stamp and a nameplate. Player guild halls are ordinary deeds
 * (StructureRenderModule). The guild tables are subscribed here once (they are small); the Guild window adds the
 * versona rosters when it opens.
 * Lever: `__guildWorld()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { groundYIn } from '../lib/grounding.js';
import { terrainSeat } from '../lib/terrain_seat.js';
import { deedPlacement } from '../lib/structure_seat.js';
import { modelYawOf } from '@shared/types/structures.js';
import { prefabAssetId } from '../lib/player_models.js';
import { addStructureSolid, clearStructureSolids } from '../lib/structure_solids.js';
import { registerBuildingStamps, unregisterBuildingStamps } from '../lib/terrain_stamp.js';
import { claimsFor, type ClaimView } from '../lib/guild_claims.js';
import { VERSONA_HALL_SCALE, VERSONA_HALL_FOOTPRINT, TRADE_GUILDS } from '../lib/guild_rules.js';
import { nameFromTables } from '../lib/versona_names.js';
import type { AssetModule } from './AssetModule.js';
import type { PhysicsModule } from './PhysicsModule.js';

const HALL_RANGE_M = 1600;   // versona halls stand within this of the player
/** A standing hall is torn down only past this (2026-09-25): with one edge, standing near 1,600 m spawned and despawned a
 *  hall — collider, solids, terrain stamp and restamp — every second. */
const HALL_KEEP_M = 1800;
const RIM_RANGE_M = 1200;    // claims whose rim is marked
const PLATE_RANGE_M = 260;
const POST_SPACING_M = 16;
const MAX_POSTS = 1200;
/** Solid-registry site ids for versona halls: well below every settlement id. */
const SOLID_SITE_BASE = -900_000;

interface VGuildRow { vguildId: bigint; settlementId: bigint; trade: string; name: string; tag: string; worldId: bigint; x: number; z: number; yaw: number; tier: number; masterVersona: bigint }
interface Hall { obj: THREE.Object3D; plate: THREE.Sprite; plateKey: string; stamp: { x: number; z: number }; top: number; x: number; z: number }

function canvasSprite(w: number, h: number, worldW: number): THREE.Sprite {
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = markMrtOverlay(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }));
  const s = new THREE.Sprite(mat);
  s.scale.set(worldW, worldW * (h / w), 1);
  s.renderOrder = 5;
  return s;
}

/** The flag colour of a claim: your guild, another player guild, or the trade's banner hue. */
export function claimColor(c: { kind: string; member: boolean; trade: string }): THREE.Color {
  if (c.kind === 'player') return new THREE.Color(c.member ? 0x5fd8ff : 0xb68cff);
  const hue = TRADE_GUILDS[c.trade]?.hue ?? 45;
  return new THREE.Color().setHSL(hue / 360, 0.6, 0.5);
}

export class GuildWorldModule implements IModule {
  readonly name = 'GuildWorld';
  private scene!: THREE.Scene;
  private readonly root = new THREE.Group();
  private master: THREE.Object3D | null = null;
  private masterBox = new THREE.Box3();
  private loading = false;
  private halls = new Map<string, Hall>();
  private poles: THREE.InstancedMesh | null = null;
  private flags: THREE.InstancedMesh | null = null;
  private rimSig = '';
  private posts = 0;
  private acc = 0;
  private subbed = false;

  constructor(private readonly assets: AssetModule | null = null, private readonly physics: PhysicsModule | null = null) {}

  init(app: App): void {
    this.scene = app.world.scene;
    this.root.name = 'guild-world';
    this.scene.add(this.root);
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    bus.on('atlas:world-cleared', () => this.clearAll());
    app.world.addSystem(makeSystem('AtlasGuildWorld', Phase.Animation, 43, (dt: number) => {
      this.acc += dt;
      if (this.acc < 1) return;
      this.acc = 0;
      try { this.tick(); } catch (e) { console.warn('[guild-world]', e); }
    }));
    (window as unknown as { __guildWorld?: unknown }).__guildWorld = () => this.diag();
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    try {
      conn.subscriptionBuilder().subscribe([
        'SELECT * FROM guild', 'SELECT * FROM guild_member', 'SELECT * FROM guild_territory', 'SELECT * FROM guild_court',
        'SELECT * FROM guild_structure', 'SELECT * FROM versona_guild',
      ]);
    } catch { /* pre-publish server */ }
  }

  private commons(conn: NonNullable<typeof atlas.conn>): { worldId: bigint; seed: string } | null {
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return { worldId: w.worldId, seed: w.terrainSeed };
    return null;
  }

  private tick(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const cm = this.commons(conn);
    const here = atlas.currentWorldId;
    if (!cm || here === null || here !== cm.worldId) { this.clearAll(); return; }
    const px = atlas.localX, pz = atlas.localZ;
    this.ensureMaster(conn);
    // Versona halls within range.
    const rows = (conn.db as unknown as { versonaGuild?: { iter(): Iterable<VGuildRow> } }).versonaGuild;
    const want = new Set<string>();
    if (rows) {
      for (const v of rows.iter()) {
        if (v.worldId !== cm.worldId) continue;
        const key = String(v.vguildId);
        const d = Math.hypot(v.x - px, v.z - pz);
        if (d > (this.halls.has(key) ? HALL_KEEP_M : HALL_RANGE_M)) continue; // spawn inside 1,600 m, keep to 1,800 m (hysteresis)
        want.add(key);
        if (!this.halls.has(key) && this.master) this.spawnHall(conn, cm.seed, v);
        const h = this.halls.get(key);
        if (h) this.updatePlate(conn, h, v, d);
      }
    }
    for (const key of [...this.halls.keys()]) if (!want.has(key)) this.despawnHall(key);
    // The rims.
    const claims = claimsFor(conn, conn.identity ?? null, cm.worldId).filter((c) => Math.hypot(c.x - px, c.z - pz) < RIM_RANGE_M + c.r);
    const sig = claims.map((c) => `${c.key}:${c.r}:${c.member ? 1 : 0}`).sort().join('|');
    if (sig !== this.rimSig) { this.rimSig = sig; this.buildRims(conn, cm.worldId, claims); }
  }

  private ensureMaster(conn: NonNullable<typeof atlas.conn>): void {
    if (this.master || this.loading || !this.assets) return;
    const id = prefabAssetId(conn, 'structure:guild_hall');
    if (id === null) return;
    this.loading = true;
    this.assets.loadForTier(id)
      .then(({ object }) => { this.master = object; this.masterBox.setFromObject(object); })
      .catch((e: unknown) => { console.warn('[guild-world] the hall model did not load', e); })
      .finally(() => { this.loading = false; });
  }

  private spawnHall(conn: NonNullable<typeof atlas.conn>, seed: string, v: VGuildRow): void {
    if (!this.master) return;
    const key = String(v.vguildId);
    const footR = Math.max(VERSONA_HALL_FOOTPRINT.hx, VERSONA_HALL_FOOTPRINT.hz);
    const y = seed ? terrainSeat(seed, v.x, v.z, footR * 0.7, 0.25).y : groundYIn(conn, v.worldId, v.x, v.z);
    // ONE placement (lib/structure_seat): the model turned by the set's model yaw so its carved door lands on the
    // frame's −Z, which the bake turned toward the town.
    const dp = deedPlacement('structure:guild_hall', v.x, y, v.z, v.yaw + modelYawOf('guild_hall'), VERSONA_HALL_SCALE, this.masterBox);
    // The placement frame (position, yaw, scale) with the seating offset INSIDE it, as the deed renderer composes it.
    const obj = new THREE.Group();
    obj.position.set(dp.x, dp.y, dp.z);
    obj.rotation.y = dp.yaw;
    obj.scale.setScalar(dp.scale);
    const inner = this.master.clone();
    if (dp.off) inner.position.add(new THREE.Vector3(dp.off.x, dp.off.y, dp.off.z));
    obj.add(inner);
    obj.userData.sharedAsset = true; // the AssetModule owns the geometry and materials
    obj.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; } });
    this.root.add(obj);
    this.physics?.addStaticCollider(`vguild:${key}`, { shape: 'box', halfExtents: dp.boxHalf }, dp.boxT);
    addStructureSolid(SOLID_SITE_BASE - Number(v.vguildId), { x: v.x, z: v.z, hx: VERSONA_HALL_FOOTPRINT.hx, hz: VERSONA_HALL_FOOTPRINT.hz, yaw: v.yaw, base: y, top: y + VERSONA_HALL_FOOTPRINT.h });
    const st = { x: dp.footX, z: dp.footZ, hw: dp.w / 2 + 0.8, hd: dp.d / 2 + 0.8, yaw: v.yaw, h: y + 0.02 };
    const fresh = registerBuildingStamps([st]);
    if (fresh.length > 0) bus.emit('atlas:terrain-restamp', { stamps: fresh });
    const plate = canvasSprite(512, 128, 22);
    plate.position.set(v.x, y + VERSONA_HALL_FOOTPRINT.h + 4, v.z);
    this.root.add(plate);
    this.halls.set(key, { obj, plate, plateKey: '', stamp: { x: st.x, z: st.z }, top: y + VERSONA_HALL_FOOTPRINT.h, x: v.x, z: v.z });
  }

  private updatePlate(conn: NonNullable<typeof atlas.conn>, h: Hall, v: VGuildRow, dist: number): void {
    h.plate.visible = dist < PLATE_RANGE_M;
    if (!h.plate.visible) return;
    let members = 0;
    const mem = (conn.db as unknown as { versonaGuildMember?: { versonaGuildMemberByGuild?: { filter(id: bigint): Iterable<unknown> } } }).versonaGuildMember;
    try { if (mem?.versonaGuildMemberByGuild) for (const _m of mem.versonaGuildMemberByGuild.filter(v.vguildId)) members++; } catch { /* not subscribed */ }
    const master = v.masterVersona ? nameFromTables(conn, v.masterVersona) : '';
    const key = `${v.name}|${master}|${members}|${v.tier}`;
    if (key === h.plateKey) return;
    h.plateKey = key;
    const tex = (h.plate.material as THREE.SpriteMaterial).map as THREE.CanvasTexture;
    const g = (tex.image as HTMLCanvasElement).getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, 512, 128);
    g.fillStyle = 'rgba(12,10,8,0.72)';
    g.fillRect(0, 8, 512, 112);
    const hue = TRADE_GUILDS[v.trade]?.hue ?? 45;
    g.fillStyle = `hsl(${hue} 60% 55%)`;
    g.fillRect(0, 8, 8, 112);
    g.fillStyle = '#f2e8d4';
    g.font = '600 34px sans-serif';
    g.textAlign = 'center';
    g.fillText(v.name, 256, 54, 480);
    g.font = '24px sans-serif';
    g.fillStyle = '#cdbf9f';
    const bits = [master ? `Master ${master}` : 'electing a master', members ? `${members} members` : '', `claim tier ${v.tier}`].filter(Boolean);
    g.fillText(bits.join(' · '), 256, 94, 480);
    tex.needsUpdate = true;
  }

  private despawnHall(key: string): void {
    const h = this.halls.get(key);
    if (!h) return;
    this.halls.delete(key);
    h.obj.removeFromParent();
    h.plate.removeFromParent();
    (h.plate.material as THREE.SpriteMaterial).map?.dispose();
    (h.plate.material as THREE.SpriteMaterial).dispose();
    this.physics?.removeStaticCollider(`vguild:${key}`);
    clearStructureSolids(SOLID_SITE_BASE - Number(key));
    unregisterBuildingStamps(h.stamp.x, h.stamp.z);
  }

  /** Banner posts along every claim's rim in range: one instanced pole batch, one instanced flag batch. */
  private buildRims(conn: NonNullable<typeof atlas.conn>, worldId: bigint, claims: ClaimView[]): void {
    const pts: Array<{ x: number; y: number; z: number; a: number; color: THREE.Color }> = [];
    for (const c of claims) {
      const n = Math.max(8, Math.ceil((2 * Math.PI * c.r) / POST_SPACING_M));
      const color = claimColor(c);
      for (let i = 0; i < n && pts.length < MAX_POSTS; i++) {
        const a = (i / n) * Math.PI * 2;
        const x = c.x + Math.cos(a) * c.r, z = c.z + Math.sin(a) * c.r;
        pts.push({ x, y: groundYIn(conn, worldId, x, z), z, a, color });
      }
    }
    if (!this.poles) {
      const poleMat = new THREE.MeshStandardMaterial({ color: 0x6b6258, roughness: 0.8, metalness: 0.2 });
      const flagMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9, side: THREE.DoubleSide });
      pinStaticMotionVectors(poleMat); pinStaticMotionVectors(flagMat); // THE FROZEN-INSTANCE law: static posts have no velocity
      this.poles = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.09, 0.12, 3.4, 6).translate(0, 1.7, 0), poleMat, MAX_POSTS);
      this.flags = new THREE.InstancedMesh(new THREE.PlaneGeometry(1.3, 0.75).translate(0.65, 2.95, 0), flagMat, MAX_POSTS);
      this.poles.name = 'guild-rim-poles'; this.flags.name = 'guild-rim-flags';
      this.poles.frustumCulled = false; this.flags.frustumCulled = false;
      this.root.add(this.poles, this.flags);
    }
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), s = new THREE.Vector3(1, 1, 1), p = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
    pts.forEach((pt, i) => {
      p.set(pt.x, pt.y, pt.z);
      // The flag streams ALONG the rim (THE HANDEDNESS LAW, 2026-09-25): three's rotation.y = φ turns local +X to
      // (cos φ, −sin φ); the rim tangent at angle a is (−sin a, cos a), so φ = −(a + π/2). `−a` pointed every flag outward.
      q.setFromAxisAngle(up, -(pt.a + Math.PI / 2));
      m.compose(p, q, s);
      this.poles!.setMatrixAt(i, m);
      this.flags!.setMatrixAt(i, m);
      this.flags!.setColorAt(i, pt.color);
    });
    this.poles.count = pts.length; this.flags!.count = pts.length;
    this.poles.instanceMatrix.needsUpdate = true;
    this.flags!.instanceMatrix.needsUpdate = true;
    if (this.flags!.instanceColor) this.flags!.instanceColor.needsUpdate = true;
    this.poles.visible = this.flags!.visible = pts.length > 0;
    this.posts = pts.length;
  }

  private clearAll(): void {
    for (const key of [...this.halls.keys()]) this.despawnHall(key);
    if (this.poles) { this.poles.count = 0; this.poles.visible = false; }
    if (this.flags) { this.flags.count = 0; this.flags.visible = false; }
    this.rimSig = '';
    this.posts = 0;
  }

  private diag(): Record<string, unknown> {
    return {
      halls: [...this.halls.entries()].map(([k, h]) => ({ vguildId: k, x: h.x, z: h.z, top: h.top, plate: h.plate.visible ? h.plateKey : '' })),
      posts: this.posts, masterLoaded: !!this.master, rimSig: this.rimSig.split('|').filter(Boolean).length,
    };
  }
}
